import React, { useCallback, useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import qs from 'qs';

import client from 'utils/client';

import Login from './Login';
import Register from './Register';
import Validate from './Validate';

function Modal({ isOpen, onClose, modal, loginHeading, signupSource }) {
  /**
   * The current modal to be displayed.
   * @type {[string, function(string): void]} currentModal, setCurrentModal
   */
  const [currentModal, setCurrentModal] = useState(modal || 'login');

  /**
   * The Campaign ID, used for Tracking.
   * @type {[string, function(string): void]} campaignId, setCampaignId
   */
  const [campaignId, setCampaignId] = useState('');

  /**
   * The Referer, used for Tracking.
   * @type {[string, function(string): void]} referer, setReferer
   */
  const [referer, setReferer] = useState('Unisport');

  /**
   * Fetch 'landing_page' from the URL Query Parameters.
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('campaign_id')) {
      setCampaignId(searchParams.get('campaign_id'));
      /*
       * We want to save the 'campaign_id' in the localStorage, so even if the Customer refreshes
       * the page, we can still track the Signup Source.
       * */
      window?.localStorage?.setItem?.('signup_time', new Date().toISOString());
      window?.localStorage?.setItem?.(
        'signup_campaign_id',
        searchParams.get('campaign_id')
      );
    } else if (
      window?.localStorage?.getItem?.('signup_time') &&
      window?.localStorage?.getItem?.('signup_campaign_id')
    ) {
      /*
       * If the Customer has the 'signup_campaign_id' in the localStorage, but it's not in the URL Query Parameters,
       * we want to check if the 'signup_time' is less than 1 hour(s) old. If it is, we can use the
       * 'signup_campaign_id' from the localStorage, to track the Signup Source.
       *
       * In case the 'signup_time' is more than 1 hour(s) old, we want to remove the 'signup_campaign_id' and the
       * 'signup_time' from the localStorage, as it is no longer valid.
       * */
      const landingPageTime = new Date(
        window?.localStorage?.getItem?.('signup_time') || ''
      );
      const currentTime = new Date();
      const deltaTime = currentTime.getTime() - landingPageTime.getTime();
      const deltaHours = Math.floor(deltaTime / (1000 * 60 * 60));
      if (deltaHours <= 1) {
        setCampaignId(window?.localStorage?.getItem?.('signup_campaign_id'));
      } else {
        window?.localStorage?.removeItem?.('signup_time');
        window?.localStorage?.removeItem?.('signup_campaign_id');
      }
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('referer')) {
      setReferer(searchParams.get('referer'));

      window?.localStorage?.setItem?.('signup_time', new Date().toISOString());
      window?.localStorage?.setItem?.(
        'signup_referer',
        searchParams.get('referer')
      );
    } else if (
      window?.localStorage?.getItem?.('signup_time') &&
      window?.localStorage?.getItem?.('signup_referer')
    ) {
      const landingPageTime = new Date(
        window?.localStorage?.getItem?.('signup_time') || ''
      );
      const currentTime = new Date();
      const deltaTime = currentTime.getTime() - landingPageTime.getTime();
      const deltaHours = Math.floor(deltaTime / (1000 * 60 * 60));
      if (deltaHours <= 1) {
        setReferer(window?.localStorage?.getItem?.('signup_referer'));
      } else {
        window?.localStorage?.removeItem?.('signup_time');
        window?.localStorage?.removeItem?.('signup_referer');
      }
    }
  }, []);

  /**
   * The current authetication configuration returned by the API.
   * @type {[{email: string, backend: string, partialToken: string}, function({email: string, backend: string, partialToken: string}): void]} authConfig, setAuthConfig
   */
  const [authConfig, setAuthConfig] = useImmer({
    email: '',
    backend: '',
    partialToken: ''
  });

  /**
   * Updates the authetication configuration based on the query string.
   * @param {URLSearchParams} searchParams - The URLSearchParams object.
   * @type {(function(URLSearchParams): void)}
   */
  const updateAuthConfig = useCallback(searchParams => {
    if (
      searchParams.has('email') &&
      searchParams.has('backend') &&
      searchParams.has('partial_token')
    ) {
      setAuthConfig(draft => {
        draft.email = searchParams.get('email');
        draft.backend = searchParams.get('backend');
        draft.partialToken = searchParams.get('partial_token');
      });
    }
  }, []);

  /**
   * Fetch 'email', 'backend' and 'partial_token' from the URL Query Parameters, if the current
   * modal is 'validate'. This is used to validate the identity of the user, if they have been
   * redirected from a Third-Party Provider.
   *
   * If the current modal is 'validate' and the URL Query Parameters are not present, then
   * redirect the user to the 'login' modal, as they have not been redirected from a Third-Party
   * Provider.
   *
   * @return {void}
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (
      searchParams.has('email') &&
      searchParams.has('backend') &&
      searchParams.has('partial_token')
    ) {
      updateAuthConfig(searchParams);
    } else if (currentModal === 'validate') {
      setCurrentModal('login');
      window.history.replaceState({}, document.title, '/account/login/');
    }
  }, []);

  /**
   * The initial OTP Array.
   * @type {[string[], function(string[]): void]} initialOTP
   */
  const initialOTP = new Array(6).fill('');

  /**
   * The OTP Array.
   * @type {[string[], function(string[]): void]} OTP, setOTP
   */
  const [OTP, setOTP] = useState(initialOTP);

  /**
   * Determines whether the Validate Component is currently validating the OTP Code.
   * @type {[boolean, function(boolean): void]} isValidatingCode, setValidatingCode
   */
  const [isValidatingCode, setValidatingCode] = useState(false);

  /**
   * Determines a general error message, which is displayed in the Modals.
   * @type {[string, function(string): void]} errorMessage, setErrorMessage
   */
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * Resets the state and calls the onClose callback.
   * @type {(function(): void)|*}
   */
  const handleClose = useCallback(() => {
    setErrorMessage('');
    setCurrentModal('login');
    onClose();
  }, [onClose]);

  /**
   * Handles the Email Backend Login.
   * @param {import('formik').FormikValues} values - The values of the Email Authentication Form.
   * @param {string} url - The URL of the API Endpoint for Email Authentication.
   * @param {func} setSubmitting - The Formik setSubmitting function.
   * @type {function(values: import('formik').FormikValues, url: string, setSubmitting: func): void}
   * @returns {void}
   */
  const handleEmailLogin = useCallback(
    async (values, url, setSubmitting) => {
      // Check if the user exists in the database.
      const response = await client
        .post(
          '/api/auth/user_exists/',
          qs.stringify({ username: values.email.trim().toLowerCase() })
        )
        .catch(() => {
          setSubmitting(false);
          setErrorMessage(
            t`An error occurred. Please try again or contact customer service.`
          );
        });

      if (!response?.data?.data) {
        setSubmitting(false);
        setErrorMessage(
          t`An error occurred. Please try again or contact customer service.`
        );
        return;
      }

      if (response?.data?.data?.exists) {
        client
          .post(
            url,
            qs.stringify({
              email: values.email.trim().toLowerCase(),
              send_code: true,
              campaign_id: campaignId,
              referer,
              signup_source: signupSource,
              landing_page: window?.location?.pathname || ''
            })
          )
          .then(result => {
            if (result?.status === 200 && result?.data) {
              const { data } = result;
              setAuthConfig(draft => {
                draft.email = data.email.trim().toLowerCase();
                draft.backend = data.backend;
                draft.partialToken = data.partial_token;
              });
              setCurrentModal('validate');
            }
          })
          .catch(() => {
            setSubmitting(false);
            setErrorMessage(
              t`An error occurred. Please try again or contact customer service.`
            );
          });
      } else {
        setAuthConfig(draft => {
          draft.email = values.email.trim().toLowerCase();
        });
        setCurrentModal('register');
      }
    },
    [campaignId, referer, signupSource]
  );

  /**
   * Handles the Google Backend Login.
   * @param {string} url - The URL of the API Endpoint for Google OAuth2 Authentication.
   * @type {function(url: string): void}
   * @returns {void}
   */
  const handleGoogleLogin = useCallback(
    url => {
      if (
        window.location.pathname !== '/account/login/' ||
        new URLSearchParams(window.location.search).has('next')
      ) {
        window.location.href = `${url}?send_code=true&next=${
          new URLSearchParams(window.location.search).get('next') ||
          window.location.pathname
        }&campaign_id=${campaignId}&referer=${referer}&signup_source=${signupSource}&landing_page=${
          window?.location?.pathname || ''
        }`;
      } else {
        window.location.href = `${url}?send_code=true&campaign_id=${campaignId}&referer=${referer}&signup_source=${signupSource}&landing_page=${
          window?.location?.pathname || ''
        }`;
      }
    },
    [campaignId, referer, signupSource]
  );

  /**
   * Handles the Email Backend Registration.
   * @param {import('formik').FormikValues} values - The values of the Email Authentication Form.
   * @param {string} url - The URL of the API Endpoint for Email Authentication.
   * @param {func} setSubmitting - The Formik setSubmitting function.
   * @type {function(values: import('formik').FormikValues, url: string, setSubmitting: func): void}
   * @returns {void}
   */
  const handleEmailRegistration = useCallback(
    async (values, url, setSubmitting) => {
      client
        .post(
          url,
          qs.stringify({
            email: values.email.trim().toLowerCase(),
            send_code: true,
            campaign_id: campaignId,
            referer,
            signup_source: signupSource,
            landing_page: window?.location?.pathname || ''
          })
        )
        .then(response => {
          if (response.status === 200) {
            const { data } = response;
            setAuthConfig(draft => {
              draft.email = data.email.trim().toLowerCase();
              draft.backend = data.backend;
              draft.partialToken = data.partial_token;
            });
            setCurrentModal('validate');
          }
        })
        .catch(() => {
          setSubmitting(false);
          setErrorMessage(
            t`An error occurred. Please try again or contact customer service.`
          );
        });
    },
    [campaignId, referer, signupSource]
  );

  /**
   * Resends a verification code to the user's email.
   * @type {(function(): void)}
   */
  const handleResendCode = useCallback(async () => {
    let url;

    const { email, backend, partialToken } = authConfig;

    switch (backend) {
      case 'google-oauth2':
        url = '/auth/api/complete/google-oauth2/';
        break;
      case 'email':
        url = '/auth/api/complete/email/';
        break;
      default:
        url = '';
        break;
    }

    client
      .post(
        url,
        qs.stringify({
          email: email.trim().toLowerCase(),
          partial_token: partialToken,
          send_code: true,
          campaign_id: campaignId,
          referer,
          signup_source: signupSource,
          landing_page: window?.location?.pathname || ''
        })
      )
      .then(response => {
        if (response.status === 200) {
          if (backend === 'email' && response.data) {
            const { data } = response;
            setAuthConfig(draft => {
              draft.email = data.email;
              draft.backend = data.backend;
              draft.partialToken = data.partial_token;
            });
          } else if (backend !== 'email' && response?.request?.responseURL) {
            const searchParams = new URLSearchParams(
              new URL(response.request.responseURL).search
            );
            updateAuthConfig(searchParams);
          }
          setCurrentModal('validate');
        }
      })
      .catch(() => {
        setErrorMessage(
          t`An error occurred. Please try again or contact customer service.`
        );
      });
  }, [authConfig, campaignId, referer, signupSource]);

  /**
   * Validates the Code that the user has submitted.
   * @param {string} code - The Code that the user has submitted.
   * @param {string} email - The Email Address of the user.
   * @param {string} backend - The Backend used for Authentication.
   * @param {string} partialToken - The Partial Token used for Authentication.
   * @type {(function(code: any, email: string, backend: string, partial_token: string,
   *   setSubmitting: function): void)}
   * @returns {void}
   */
  const handleCodeValidation = useCallback(
    (code, email, backend, partialToken, setSubmitting) => {
      let url;

      setSubmitting(true);

      switch (backend) {
        case 'google-oauth2':
          url = '/auth/api/complete/google-oauth2/';
          break;
        case 'email':
          url = '/auth/api/complete/email/';
          break;
        default:
          url = '';
          break;
      }

      client
        .post(
          url,
          qs.stringify({
            code,
            email: email.trim().toLowerCase(),
            partial_token: partialToken,
            campaign_id: campaignId,
            referer,
            signup_source: signupSource,
            landing_page: window?.location?.pathname || ''
          }),
          {
            validateStatus: status => status === 200 || status === 400
          }
        )
        .then(response => {
          if (response?.status === 200) {
            const searchParams = new URLSearchParams(window.location.search);

            if (searchParams.has('next')) {
              window.location.href = searchParams.get('next');
            } else {
              window?.location?.reload();
            }
          } else if (response?.status === 400) {
            setOTP(initialOTP);
            setErrorMessage(
              t`You have entered an invalid code. Please try again.`
            );
            if (response?.data?.partial_token) {
              setAuthConfig(draft => {
                draft.partialToken = response.data.partial_token;
              });
            }
            setSubmitting(false);
          }
        })
        .catch(() => {
          setErrorMessage(
            t`An error occurred. Please try again or contact customer service.`
          );
        });
    },
    [campaignId, referer, signupSource]
  );

  if (currentModal === 'login') {
    return (
      <Login
        isOpen={isOpen}
        onClose={() => handleClose()}
        handleEmailLogin={(values, url, setSubmitting) =>
          handleEmailLogin(values, url, setSubmitting)
        }
        handleGoogleLogin={url => handleGoogleLogin(url)}
        loginHeading={loginHeading}
      />
    );
  }

  if (currentModal === 'register') {
    return (
      <Register
        isOpen={isOpen}
        onClose={() => handleClose()}
        authConfig={authConfig}
        handleEmailRegistration={(values, url, setSubmitting) =>
          handleEmailRegistration(values, url, setSubmitting)
        }
        errorMessage={errorMessage}
      />
    );
  }

  if (currentModal === 'validate') {
    return (
      <Validate
        isOpen={isOpen}
        onClose={() => handleClose()}
        OTP={OTP}
        setOTP={setOTP}
        isValidatingCode={isValidatingCode}
        handleResendCode={() => handleResendCode()}
        handleCodeValidation={code =>
          handleCodeValidation(
            code,
            authConfig.email,
            authConfig.backend,
            authConfig.partialToken,
            setValidatingCode
          )
        }
        errorMessage={errorMessage}
      />
    );
  }

  return null;
}

Modal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  modal: PropTypes.oneOf(['login', 'register', 'validate']),
  loginHeading: PropTypes.string,
  signupSource: PropTypes.string
};

Modal.defaultProps = {
  isOpen: false,
  onClose: () => {},
  modal: 'login',
  loginHeading: t`Log in or become a member`,
  signupSource: 'profile'
};

export default Modal;
